import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Truck, ShieldCheck, Users, Headphones, ArrowRight, ShoppingBasket, Shirt, Smartphone, Sofa, Sparkles, Palette, SlidersHorizontal, Wallet, CheckCircle2, MapPin, Search } from "lucide-react";
import { LocationCascade } from "@/components/LocationCascade";
import { GeoShopping } from "@/components/GeoShopping";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FEATURE_INFO } from "@/lib/featureInfo";

const ICONS = { ShoppingBasket, Shirt, Smartphone, Sofa, Sparkles, Palette };
const STRIP = [
  { icon: ShieldCheck, k: "strip_secure", color: "text-emerald-600" },
  { icon: Wallet, k: "strip_pay", color: "text-mango-600" },
  { icon: Truck, k: "strip_delivery", color: "text-sky-500" },
  { icon: Users, k: "strip_all", color: "text-amber-500" },
  { icon: Headphones, k: "strip_support", color: "text-rose-500" },
];
const HERO_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/a54c8694330ebb6639b89b52de3fd2ce0df3304262622955b824e95f1ab8ba89.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/27d487069a4154d06edf764744e181264f286f50cdf58df1c0154e1b64f81071.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/c0209bf3dfac58deba1520c1719314952c4b92ec471e4183851bea62a22c25e6.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/488bd2c7bc2dcf4e78419303f9aba9817db402337c7ca3752ab84bc3883ab6b0.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/42240a7759f2f03d2838e5536ff135b1361e3f44d22ba434c164593a669c6868.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/cb3c864fcab979725b7089fe544855c328c9d24ebf7ad78d176624bf2b5e950a.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/c34da421c54f87bd23ab53fa5c1ac07106288bb18b66f92d7cfbacd51664cf8f.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/544c7075-efca-4207-a70a-f85167566c25/images/386d6d5bd29dec7009800be6194bbf91d4a8c1f7d02c9f6d855f66e938bf9408.jpeg",
  "https://images.pexels.com/photos/34523107/pexels-photo-34523107.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1655682614757-a9a33fa45c93?w=1600&q=80",
];
const CAT_IMG = {
  food: "https://images.unsplash.com/photo-1784501025488-001592b6ee31?w=500&q=80",
  fashion: "https://images.unsplash.com/photo-1655682614757-a9a33fa45c93?w=500&q=80",
  electronics: "https://images.pexels.com/photos/14486283/pexels-photo-14486283.jpeg?w=500",
  home: "https://images.pexels.com/photos/34667143/pexels-photo-34667143.jpeg?w=500",
  beauty: "https://images.unsplash.com/photo-1774620714979-2c258294fa48?w=500&q=80",
  crafts: "https://images.unsplash.com/photo-1621419203897-20b66b98d495?w=500&q=80",
};

export default function Home() {
  const { t, lang } = useLang();
  const { user, openAuth } = useAuth();
  const [featured, setFeatured] = useState(null);
  const [recent, setRecent] = useState(null);
  const [recs, setRecs] = useState([]);
  const [layout, setLayout] = useState({ featured_count: 8, recommendations_count: 4, recent_count: 8 });
  const [homeSections, setHomeSections] = useState([]);
  const [sectionData, setSectionData] = useState({});
  const [cats, setCats] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [activeFeature, setActiveFeature] = useState(null);
  const isBrandSlide = HERO_IMAGES[heroIdx].startsWith("/banners/");
  const [locSel, setLocSel] = useState({});
  const navigate = useNavigate();
  const applyLoc = () => {
    const qs = new URLSearchParams();
    ["region", "prefecture", "commune", "canton", "localite"].forEach((k) => locSel[k] && qs.set(k, locSel[k]));
    navigate(`/catalog?${qs.toString()}`);
  };

  useEffect(() => {
    const _id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(_id);
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/settings/home")
      .then((r) => r.data)
      .catch(() => ({ featured_count: 8, recommendations_count: 4, recent_count: 8, sections: [] }))
      .then((L) => {
        setLayout(L);
        api.get(`/products?featured=true&limit=${L.featured_count}`).then((r) => setFeatured(r.data));
        api.get(`/products?sort=newest&limit=${L.recent_count}`).then((r) => setRecent(r.data));
        api.get(`/recommendations?limit=${L.recommendations_count}`).then((r) => setRecs(r.data)).catch(() => {});
        const secs = L.sections || [];
        setHomeSections(secs);
        secs.forEach((s) => {
          const qs = new URLSearchParams({ sort: s.sort || "newest", limit: String(s.count || 8) });
          if (s.category) qs.set("category", s.category);
          api.get(`/products?${qs.toString()}`).then((r) => setSectionData((d) => ({ ...d, [s.id]: r.data }))).catch(() => {});
        });
      });
  }, [user]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden min-h-[110vh] flex items-center">
        <div className="absolute inset-0">
          {HERO_IMAGES.map((src, i) => (
            <img key={i} src={src} alt="Marché de Lomé" loading={i === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === heroIdx ? "opacity-100" : "opacity-0"}`} />
          ))}
          {!isBrandSlide && <div className="absolute inset-0 bg-gradient-to-r from-stone-900/75 via-stone-900/45 to-stone-900/15" />}
        </div>
        {!isBrandSlide && (
        <div className="relative w-full max-w-7xl mx-auto px-6 py-12">
          <div className="max-w-2xl fade-up">
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="hero-badges">
              {[{ label: t("badge_buy"), key: "buy" }, { label: t("badge_sell"), key: "sell" }, { label: t("badge_grow"), key: "grow" }].map(({ label, key }) => (
                <button key={key} onClick={() => setActiveFeature(key)} data-testid={`hero-badge-${key}`}
                  className="inline-flex items-center rounded-full border border-white/40 bg-white/15 hover:bg-white hover:text-emerald-900 backdrop-blur-md text-white px-4 py-1 text-xs font-bold uppercase tracking-widest transition-colors cursor-pointer">{label}</button>
              ))}
            </div>
            <p className="font-script text-white text-4xl sm:text-5xl leading-none mb-3 drop-shadow-lg [text-shadow:_0_2px_8px_rgb(0_0_0_/_45%)]" data-testid="hero-tagline">{t("brand_tagline")}</p>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-none tracking-tighter mb-5">{t("hero_title")}</h1>
            <p className="text-stone-200 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">{t("hero_sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/catalog"><Button size="lg" className="rounded-full bg-mango-600 hover:bg-mango-700 h-12 px-7 text-base" data-testid="hero-shop">{t("shop_now")} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              {(!user || user.role === "buyer") && <Button size="lg" variant="outline" onClick={() => openAuth("register", "seller")} className="rounded-full h-12 px-7 text-base bg-white/10 text-white border-white/40 hover:bg-white/20" data-testid="hero-seller">{t("become_seller")}</Button>}
            </div>
          </div>
        </div>
        )}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="hero-dots">
          {HERO_IMAGES.map((_, i) => (
            <button key={i} onClick={() => setHeroIdx(i)} aria-label={`slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === heroIdx ? "w-7 bg-mango-500" : "w-2 bg-white/50 hover:bg-white/80"}`} />
          ))}
        </div>
      </section>

      {/* Trust / feature strip (clickable) */}
      <section className="w-full bg-emerald-800 text-white" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-6 gap-x-4">
          {STRIP.map(({ icon: Icon, k, color }) => (
            <button key={k} onClick={() => setActiveFeature(k)} data-testid={`feature-btn-${k}`}
              className="flex items-center gap-3 text-left group rounded-xl p-2.5 transition-all duration-200 hover:bg-white hover:shadow-lg">
              <span className="w-10 h-10 rounded-full bg-white group-hover:bg-emerald-50 grid place-items-center shrink-0 shadow-sm transition-all group-hover:scale-110"><Icon className={`w-5 h-5 ${color}`} /></span>
              <div>
                <p className="font-bold text-sm uppercase tracking-wider leading-tight text-white group-hover:text-emerald-900 transition-colors">{t(k)}</p>
                <p className="text-xs text-emerald-100/80 group-hover:text-stone-600 leading-tight mt-0.5 transition-colors">{t(k + "_d")}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* GeoShopping interactive map */}
      <GeoShopping lang={lang} />

      {/* Location filter */}
      <section className="w-full px-4 sm:px-6 lg:px-10 py-10" data-testid="home-location-filter">
        <div className="bg-white border border-stone-200 rounded-2xl shadow-md overflow-hidden">
          <div className="bg-emerald-800 text-white px-6 py-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-mango-600 grid place-items-center shrink-0"><MapPin className="w-5 h-5" /></span>
            <div>
              <p className="font-display font-extrabold text-lg leading-none">{t("filter_by_location")}</p>
              <p className="text-emerald-100 text-xs mt-1">{t("location_hint")}</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <LocationCascade value={locSel} onChange={setLocSel} layout="row" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={applyLoc} className="bg-mango-600 hover:bg-mango-700 rounded-full px-6" data-testid="home-loc-search"><Search className="w-4 h-4 mr-2" />{t("apply_filter")}</Button>
              <Button variant="outline" onClick={() => setLocSel({})} className="rounded-full" data-testid="home-loc-reset">{t("reset")}</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Horizontal quick filters */}
      <section className="w-full px-4 sm:px-6 lg:px-10 mt-14" data-testid="home-filter-bar">
        <div className="bg-[#065F46] rounded-2xl shadow-xl ring-1 ring-black/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-lg bg-mango-600 text-white grid place-items-center shadow-sm"><SlidersHorizontal className="w-5 h-5" /></span>
            <h3 className="font-display font-extrabold text-xl tracking-tight text-white">{t("quick_filters")}</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Link to="/catalog" className="shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold bg-mango-600 text-white hover:bg-mango-700 hover:scale-105 transition-all shadow-sm">{t("all")}</Link>
            {cats.map((c) => {
              const Icon = ICONS[c.icon] || ShoppingBasket;
              return (
                <Link key={c.key} to={`/catalog?category=${c.key}`} data-testid={`home-filter-${c.key}`}
                  className="shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium bg-white/10 border border-white/20 text-white hover:bg-white hover:text-emerald-900 transition-all flex items-center gap-2 backdrop-blur">
                  <Icon className="w-4 h-4 text-mango-400" /> {c[lang]}
                </Link>
              );
            })}
            <span className="shrink-0 w-px h-6 bg-white/20 mx-1" />
            <span className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-emerald-200 uppercase tracking-wider mr-1"><Wallet className="w-3.5 h-3.5" /> {t("price")}</span>
            <Link to="/catalog?max=5000" className="shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium bg-white/10 border border-white/25 text-white hover:bg-mango-600 hover:border-mango-600 transition-all">{t("price_under")} 5 000</Link>
            <Link to="/catalog?min=5000&max=20000" className="shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium bg-white/10 border border-white/25 text-white hover:bg-mango-600 hover:border-mango-600 transition-all">5 000 – 20 000</Link>
            <Link to="/catalog?min=20000" className="shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium bg-white/10 border border-white/25 text-white hover:bg-mango-600 hover:border-mango-600 transition-all">{t("price_over")} 20 000</Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-mango-600 text-xs font-bold uppercase tracking-widest">{t("nav_catalog")}</span>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-1">{t("categories")}</h2>
          </div>
          <Link to="/catalog" className="text-sm text-emerald-800 font-semibold hover:text-mango-600 transition-colors shrink-0">{t("view_all")} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cats.map((c, ci) => {
            const Icon = ICONS[c.icon] || ShoppingBasket;
            return (
              <Link key={c.key} to={`/catalog?category=${c.key}`} data-testid={`category-${c.key}`}
                className="group relative overflow-hidden rounded-2xl aspect-[4/5] shadow-md card-hover img-zoom-wrap fade-up"
                style={{ animationDelay: `${ci * 60}ms` }}>
                <img src={mediaUrl(c.image) || CAT_IMG[c.key] || "https://images.pexels.com/photos/37904067/pexels-photo-37904067.jpeg?auto=compress&cs=tinysrgb&w=800"} alt={c[lang]} className="absolute inset-0 w-full h-full object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-900/30 to-transparent" />
                <span className="absolute top-3 left-3 w-9 h-9 rounded-xl bg-white/90 backdrop-blur text-emerald-800 grid place-items-center shadow-sm group-hover:bg-mango-600 group-hover:text-white transition-colors">
                  <Icon className="w-4.5 h-4.5" />
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="font-display font-bold text-white text-base leading-tight">{c[lang]}</p>
                  <p className="text-[11px] text-white/85 font-semibold mt-0.5" data-testid={`category-count-${c.key}`}>{c.product_count ?? 0} {(c.product_count ?? 0) === 1 ? (lang === "en" ? "product" : "produit") : (lang === "en" ? "products" : "produits")}</p>
                  <span className="inline-flex items-center gap-1 text-[11px] text-mango-300 font-semibold mt-1 opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all">{t("shop_now")} →</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Spotlight */}
      <section className="max-w-7xl mx-auto px-6 mt-20">
        <div className="grid md:grid-cols-2 gap-8 items-center bg-emerald-800 rounded-3xl overflow-hidden grain">
          <div className="p-10 md:p-14 text-white">
            <span className="text-mango-400 text-xs font-bold uppercase tracking-widest">Spotlight</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 mb-4 tracking-tight">{t("spotlight_title")}</h2>
            <p className="text-emerald-100 leading-relaxed mb-6">{t("spotlight_sub")}</p>
            <Link to="/catalog"><Button className="rounded-full bg-mango-600 hover:bg-mango-700">{t("shop_now")}</Button></Link>
          </div>
          <div className="h-64 md:h-80 img-zoom-wrap overflow-hidden">
            <img src="https://images.unsplash.com/photo-1734255026082-82fdc81991f0?w=800&q=80" alt="Marché" className="w-full h-full object-cover img-zoom" />
          </div>
        </div>
      </section>

      {/* Featured */}
      <ProductSection title={t("featured")} products={featured} count={layout.featured_count} />

      {/* Recommendations */}
      {recs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 mt-20">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mb-6 flex items-center gap-2"><Sparkles className="w-6 h-6 text-mango-600" /> {t("recommended")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {recs.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* New arrivals */}
      <ProductSection title={t("new_arrivals")} products={recent} count={layout.recent_count} />

      {/* Custom admin-defined sections */}
      {homeSections.map((s) => {
        const data = sectionData[s.id];
        if (data && data.length === 0) return null;
        return <ProductSection key={s.id} title={lang === "en" && s.title_en ? s.title_en : s.title_fr} products={data ?? null} count={s.count} />;
      })}

      {/* Brand showcase before footer */}
      <section className="w-full bg-white mt-20 py-16" data-testid="brand-showcase">
        <div className="max-w-4xl mx-auto px-6 flex flex-col items-center">
          <Link to="/catalog" className="group flex flex-col items-center" data-testid="brand-showcase-link" aria-label={t("nav_catalog")}>
            <img src="/banners/agoo-brand2.png?v=2" alt="agoomarket" className="w-full max-w-2xl h-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-mango-700 group-hover:text-mango-800 transition-colors" data-testid="brand-showcase-caption">
              <ShoppingBasket className="w-4 h-4" />
              {lang === "en" ? "Click here to open the shop and browse all products" : "Cliquez ici pour ouvrir la boutique et parcourir tous les produits"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        </div>
      </section>

      <FeatureDialog activeKey={activeFeature} onClose={() => setActiveFeature(null)} lang={lang} t={t} />
    </div>
  );
}

const FeatureDialog = ({ activeKey, onClose, lang, t }) => {
  const info = activeKey ? FEATURE_INFO[activeKey]?.[lang] : null;
  const Icon = STRIP.find((s) => s.k === activeKey)?.icon || { buy: ShoppingBasket, sell: Wallet, grow: Users }[activeKey] || ShoppingBasket;
  return (
    <Dialog open={!!activeKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border-0 gap-0" data-testid="feature-modal">
        {info && (
          <>
            <div className="relative bg-emerald-800 text-white p-6 grain overflow-hidden">
              <span className="absolute top-0 right-0 w-40 h-40 bg-mango-500/20 rounded-full blur-3xl -mr-12 -mt-12" />
              <span className="relative w-14 h-14 rounded-2xl bg-white/10 grid place-items-center mb-3"><Icon className="w-7 h-7 text-mango-400" /></span>
              <DialogTitle className="font-display font-extrabold text-2xl tracking-tight">{info.title}</DialogTitle>
              <p className="text-emerald-100 text-sm mt-1">{info.subtitle}</p>
            </div>
            <div className="p-6 bg-sand-50 space-y-6 max-h-[60vh] overflow-y-auto">
              <p className="text-stone-600 leading-relaxed" data-testid="feature-desc">{info.desc}</p>
              <div>
                <h3 className="font-display font-bold text-emerald-800 mb-3 flex items-center gap-2 uppercase text-sm tracking-wider"><SlidersHorizontal className="w-4 h-4 text-mango-600" /> {t("how_it_works")}</h3>
                <ol className="space-y-2.5">
                  {info.how.map((s, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <span className="w-6 h-6 rounded-full bg-mango-600 text-white text-xs font-bold grid place-items-center shrink-0">{i + 1}</span>
                      <span className="text-sm text-stone-700 leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div>
                <h3 className="font-display font-bold text-emerald-800 mb-3 flex items-center gap-2 uppercase text-sm tracking-wider"><Sparkles className="w-4 h-4 text-mango-600" /> {t("features_provided")}</h3>
                <ul className="space-y-2">
                  {info.functions.map((f, i) => (
                    <li key={i} className="flex gap-2 items-start">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                      <span className="text-sm text-stone-700 leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ProductSection = ({ title, products, count = 8 }) => (
  <section className="max-w-7xl mx-auto px-6 mt-20">
    <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">{title}</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {products === null
        ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)
        : products.slice(0, count).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
    </div>
  </section>
);
